<template>
  <div class="ed-table-wrapper">
    <TableSearch
      v-if="search"
      class="mb-2 fadeIn"
      :value="searchValue"
      @SEARCH="handleSearch"
    />
    <div v-if="!tableLoaded" class="loader-wrapper">
      <div class="loader"></div>
    </div>
    <div v-else style="height: 5px"></div>
    <table class="ed-table">
      <thead>
        <tr class="bg-white fadeIn">
          <TableHeader
            v-for="(item, index) in tableHeaders"
            :key="index + '_header'"
            :descOrder="descOrder"
            :textContent="typeof item === 'object' ? item.textContent : item"
            :classList="item.classList"
            :sortingParam="item.sortingParam"
            :currentSortingParamValue="currentSortingParamValue"
            @SORT_BY="updatedSortby"
          />
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in items" :key="item.id" class="ed-table-row fadeIn">
          <slot name="row" :row="item">
            <td
              v-for="(cell, index) in getNonActionCells"
              :key="cell.path + index"
              :class="applyClasses(cell)"
              v-html="getDataFromCell(item, cell)"
            ></td>
            <td v-for="cell in getFunctionCells" :key="cell.id">
              <button
                :class="applyClasses(cell)"
                @click="handleButtonCellClick(item, cell)"
                v-html="cell.buttonText"
              ></button>
            </td>
            <td v-if="getActionCell">
              <router-link
                :to="getActionRoutePath(item, getActionCell)"
                :class="applyClasses(getActionCell)"
                v-html="getActionCell.actionButtonText"
              >
              </router-link>
            </td>
          </slot>
        </tr>
      </tbody>
    </table>
    <div
      v-if="items && items.length === 0 && tableLoaded"
      class="fadeIn text-center mt-4"
    >
      Nothing here!
    </div>
    <PaginationList
      v-if="items.length > 0 && pages > 1"
      :pages="pages"
      :page="page"
      class="justify-center mt-4"
      @click="updatePage"
    />
  </div>
</template>

<script>
import { hasKey } from "@/utils/objectUtil"
import TableHeader from "@/components/DataTable/TableHeader"
import PaginationList from "@/components/CollegeCleanup/PaginationList"
import { formatReadingTime } from "@/utils/timeFormatter"
import TableSearch from "@/components/DataTable/TableSearchRow"

export default {
  components: { TableHeader, PaginationList, TableSearch },
  props: {
    endpoint: {
      type: String,
      required: true,
    },
    tableConfig: {
      type: Array,
      required: false,
      default() {
        return [
          /** Two ways of passing config:
            1. Passing full config and let the table generate
               headers and body from the config. Sample config:
            [
              {
                header: { textContent: "Created On", sortingParam: "created_on" },
                type: "date",
                path: "created_on",
              }
            ]

            2. Passing only the headers as an array of strings
               or object for customization
            [
              { header: { textContent: "Name", sortingParam: "name" } },
              "Component",
              "Condition",
            ]
           */
        ]
      },
    },
    search: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      items: [],
      page: 1,
      perPage: 20,
      pages: 0,
      tableLoaded: false,
    }
  },
  computed: {
    searchValue() {
      if (this.$route.query && this.$route.query.search) {
        return this.$route.query.search
      }
      return ""
    },
    tableHeaders() {
      return this.tableConfig.map(col =>
        typeof col === "object" ? col.header : col
      )
    },
    descOrder() {
      return (
        this.currentSortingParamValue &&
        this.currentSortingParamValue.startsWith("-")
      )
    },
    currentSortingParamValue() {
      if (this.$route.query && this.$route.query.sort_by) {
        return this.$route.query.sort_by
      }
      return ""
    },
    getActionCell() {
      // for now returns the first action cell
      return this.tableConfig.find(cell => cell.type === "action")
    },
    getNonActionCells() {
      return this.tableConfig.filter(
        item => item.type !== "action" && item.type !== "button"
      )
    },
    getFunctionCells() {
      return this.tableConfig.filter(item => item.type === "button")
    },
  },
  watch: {
    $route() {
      this.getItems()
    },
  },
  created() {
    if (this.$route.query) {
      const { page, per_page } = this.$route.query
      if (page) {
        this.page = page
      }
      if (per_page) {
        this.perPage = per_page
      }
    }
    this.getItems()
  },
  methods: {
    getItems() {
      this.tableLoaded = false
      const url = new URL(this.endpoint)
      const searchParams = new URLSearchParams(url.search)
      /* add the query params to this URL */
      Object.keys(this.$route.query).forEach(param => {
        searchParams.set(param, this.$route.query[param])
      })
      /* default params for pagination */
      if (!searchParams.has("page")) {
        searchParams.set("page", this.page)
      }
      if (!searchParams.has("per_page")) {
        searchParams.set("per_page", this.perPage)
      }
      url.search = searchParams.toString()

      this.axios({
        method: "get",
        url: url.toString(),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          this.items = res.data.items
          this.page = res.data.page
          this.pages = res.data.pages
          this.perPage = res.data.per_page
          this.tableLoaded = true
        })
        .catch(err => {
          this.$noty.error(`Failed to fetch table, error: ${err}`)
          this.tableLoaded = true
        })
    },
    updateRoute(data) {
      const routeObj = { ...this.$route, query: { ...this.$route.query } }

      if (data && Array.isArray(data)) {
        data.forEach(item => {
          if (item.value) {
            routeObj.query[item.key] = item.value
          } else {
            Reflect.deleteProperty(routeObj.query, item.key)
          }
        })
      } else if (data && typeof data === "object") {
        if (data.value) {
          routeObj.query[data.key] = data.value
        } else {
          Reflect.deleteProperty(routeObj.query, data.key)
        }
      }
      this.$router.push(routeObj).catch(() => {})
    },
    updatePage(page) {
      this.page = page
      this.updateRoute({ key: "page", value: page })
    },
    handleButtonCellClick(obj, tableCell) {
      if (tableCell.path === "/") {
        tableCell.onclick(obj, tableCell)
        return
      }
      tableCell.onclick(this.extractDataFromResponse(obj, tableCell))
    },
    getDataFromCell(obj, tableCell) {
      // if custom formatter is given
      if (tableCell.formatter && typeof tableCell.formatter === "function") {
        if (tableCell.path === "/") {
          return tableCell.formatter(obj)
        }
        return tableCell.formatter(this.extractDataFromResponse(obj, tableCell))
      }
      if (tableCell.type === "date") {
        return this.getFormattedDate(
          this.extractDataFromResponse(obj, tableCell)
        )
      }
      if (tableCell.type === "readingTime") {
        return this.getFormattedReadingTime(
          this.extractDataFromResponse(obj, tableCell)
        )
      }
      // default type is "text"
      return this.extractDataFromResponse(obj, tableCell)
    },
    extractDataFromResponse(obj, tableCell) {
      if (
        tableCell !== null &&
        typeof tableCell === "object" &&
        hasKey(tableCell, "path")
      ) {
        if (!typeof tableCell.path === "string") {
          throw new Error("Table cell path must be string.")
        }
        if (!tableCell.path.includes(".")) {
          // not nested query
          return this.getPropertyFromObject(obj, tableCell.path)
        }
        if (!tableCell.path.includes("+")) {
          // no concatenation
          return this.getPropertyFromObject(obj, tableCell.path)
        }
        // has concatenation
        const pathList = tableCell.path.split("+")
        let str = ""
        pathList.forEach(path => {
          str += ` ${this.getPropertyFromObject(obj, path)}`
        })
        return str.trim()
      }
      return "Null"
    },
    getPropertyFromObject(obj, path) {
      path = path.split(".")
      return path.reduce(
        (currentValue, currentKey) =>
          currentValue &&
          (currentValue[currentKey] !== null ||
            currentValue[currentKey] !== undefined)
            ? currentValue[currentKey]
            : "Null",
        obj
      )
    },
    getFormattedDate(date) {
      const d = new Date(date)
      const timeStr = d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      const dateStr = d.toLocaleDateString("en-GB")
      return `${timeStr}, ${dateStr}`
    },
    getFormattedReadingTime(time) {
      if (!time) return "-" // for handling falsy values including 0
      return `${formatReadingTime(time, "mins")} min(s)`
    },
    getActionRoutePath(item, cellObj) {
      if (!cellObj.routePath)
        throw new Error("routePath is undefined for action cell")
      const arr = cellObj.routePath.split("/")
      const paramIndex = arr.findIndex(p => p.startsWith(":"))
      if (paramIndex < 0)
        throw new Error(
          "Route param not found for action cell in configuration object. It's similar to Vue router's params. ex:- /coding/:codingId"
        )
      arr[paramIndex] = this.extractDataFromResponse(item, cellObj)
      return arr.join("/")
    },
    applyClasses(cell) {
      return cell.classList ? cell.classList : ""
    },
    updatedSortby(data) {
      this.updateRoute({ key: "sort_by", value: data.value })
    },
    handleSearch(data) {
      this.updateRoute([
        { key: "search", value: data.text },
        { key: "page", value: 1 }, // set page to 1 as when searched
      ])
    },
  },
}
</script>

<style scoped>
.ed-table-wrapper {
  padding: 1rem;
  border: 1px solid #0000001f;
  border-radius: 3px;
  font-size: 0.95rem;
}
.ed-table {
  border-radius: 3px;
  border-spacing: 0;
  border-collapse: separate;
  background-color: white;
  box-shadow: 0 2px 1px -1px #00000033, 0 1px 1px 0 #00000024,
    0 1px 3px 0 #0000001f;
}

.ed-table tr {
  border-bottom: 1px solid #0000001a;
}

.ed-table th {
  border-right: 1px solid #0000001a;
  color: rgba(255, 248, 248, 0.87);
  background: #555bbd;
  font-weight: bold;
}

.ed-table td {
  display: table-cell;
  padding: 0.85rem;
  border-right: 1px solid #0000001a;
  border-bottom: 1px solid #0000001a;
  color: #000000be;
  background-color: inherit;
}

.ed-table td:last-child {
  border-right: none;
}

.ed-table tr:last-child td {
  border-bottom: none;
}

.ed-table td a:active,
.ed-table td a:focus {
  outline: 2px solid #2793be;
  outline-offset: 2px;
}

.ed-table .ed-table-row {
  background-color: white;
}

.ed-table .ed-table-row:hover {
  filter: brightness(0.9);
}

.loader-wrapper {
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 5px;
  border-radius: 3px 3px 0 0;
  background: #6259c34b;
}

.loader {
  position: absolute;
  top: 0;
  right: auto;
  bottom: 0;
  left: 0;
  width: auto;
  height: 5px;
  background: #6259c3;
  animation: indeterminate-progress 2s linear infinite;
  will-change: left, right;
}

.fadeIn {
  animation: fadeIn 0.3s ease-out;
}

@keyframes indeterminate-progress {
  0% {
    right: 10%;
    left: -100%;
  }
  0% {
    right: 100%;
    left: -90%;
  }
  100% {
    right: -35%;
    left: 100%;
  }
}
</style>
